export type RetreatFormat = 'residential' | 'day' | 'weekend';

/**
 * The single list of retreats. `endDate` is load-bearing: everything customer-facing
 * derives from `getUpcomingRetreats()`, so a retreat disappears from the listing, its
 * own page and the sitemap the day after it ends. Get `endDate` wrong and it vanishes
 * early or lingers after the event — set it to the last day of the retreat, not the first.
 */
export type Retreat = {
  id: string;
  name: string;
  location: string;
  startDate: string;      // ISO "2026-05-16"
  endDate: string;        // ISO "2026-05-22" — LAST day; drives the upcoming filter
  displayDates: string;   // "16–22 May 2026"
  /** Set only for retreats with their own page under /retreats/<slug>; feeds the sitemap. */
  pageLastmod?: string;
  heroImage: string;
  description: string;
  forYouIf: string;
  bookLink: string;
  brochureLink?: string;
  teamLink?: string;
  scarcityNote?: string;
  /**
   * Availability, shown as a badge on every card that renders this retreat.
   *
   * `sold-out` is load-bearing: a sold-out retreat used to render identically to
   * a bookable one and keep sending people to the host's checkout to find out
   * for themselves (23 sessions did exactly that on the Autumn Equinox page).
   * Setting it swaps every CTA for the waitlist capture — see `isBookable`.
   */
  status?: 'nearly-sold-out' | 'booking-soon' | 'sold-out';
  /**
   * Price as a visitor should read it on a listing card, before clicking.
   *
   * Only for `partner` retreats, whose prices we don't hold in the fee config
   * because we don't take the money. CF retreats derive theirs from
   * `app/data/retreatPricing.ts` instead — never hardcode one here, that is the
   * bug that had /retreats advertising £3,000 while checkout billed £2,800.
   */
  priceLabel?: string;
  isExternalBook?: boolean;
  /**
   * What kind of retreat this is, which decides where it is listed.
   *
   * `residential` — a week away (La Manga, Portugal): `/retreats`.
   * `day` / `weekend` — the short UK retreats: `/retreats/day-retreats`.
   * Required, so a new retreat cannot land on neither page by omission.
   */
  format: RetreatFormat;
  type: 'cf' | 'partner'; // 'cf' = CF Organic Wellness owned; 'partner' = Niki collaborates
  partnerName?: string;   // e.g. "Yoga on the Edge" — shown in partner section
  partnerLogo?: string;   // path to logo in /public/images/
};

export const retreats: Retreat[] = [
  {
    id: "la-manga-june-05-2027",
    type: "cf",
    format: "residential",
    name: "The Art of Eating, Moving, Breathing & Being",
    location: "La Manga, Spain",
    startDate: "2027-06-05",
    endDate: "2027-06-11",
    displayDates: "5–11 June 2027",
    heroImage: "/images/laManga.webp",
    description:
      "The flagship retreat. Eight specialist practitioners, gut and liver cleansing protocols, IV therapy, Shiatsu, Ayurvedic massage, yoga, Wim Hof breathwork, cold exposure, and clinical hypnotherapy — all in one week in southern Spain.",
    forYouIf:
      "you want the deepest reset available, with real medical and naturopathic expertise in the room.",
    bookLink: "/retreats/book",
    brochureLink: "/images/La_Manga_2026.pdf",
    teamLink: "/retreats/team",
    scarcityNote: "Limited to 18 guests · spaces fill fast",
  },
  {
    id: "la-manga-june-12-2027",
    type: "cf",
    format: "residential",
    name: "The Art of Eating, Moving, Breathing & Being",
    location: "La Manga, Spain",
    startDate: "2027-06-12",
    endDate: "2027-06-18",
    displayDates: "12–18 June 2027",
    heroImage: "/images/laManga.webp",
    description:
      "The flagship retreat. Eight specialist practitioners, gut and liver cleansing protocols, IV therapy, Shiatsu, Ayurvedic massage, yoga, Wim Hof breathwork, cold exposure, and clinical hypnotherapy — all in one week in southern Spain.",
    forYouIf:
      "you want the deepest reset available, with real medical and naturopathic expertise in the room.",
    bookLink: "/retreats/book",
    brochureLink: "/images/La_Manga_2026.pdf",
    teamLink: "/retreats/team",
    scarcityNote: "Limited to 18 guests · spaces fill fast",
  },
  {
    id: "autumn-flamingo-sept-2026",
    type: "partner",
    format: "day",
    name: "Early Autumn Equinox Half Day Retreat",
    location: "Flamingo Falls, Mobberley",
    startDate: "2026-09-12",
    endDate: "2026-09-12",
    displayDates: "Sat 12 Sept 2026",
    heroImage: "/images/flamingo-falls-hero.webp",
    description:
      "A gentle half-day retreat at Flamingo Falls to mark the shift into autumn — flowing yoga, restorative practice, nature immersion, and seasonal nutrition with Niki Charnock and Clare Broomhead.",
    forYouIf:
      "you want to slow down, soak up the last of the summer light, and set clear intentions for the season ahead.",
    // Its own page (/retreats/autumn-equinox-sept-2026) was retired after the
    // event and 301s to the day-retreats page, which lists it as a past retreat.
    bookLink: "/retreats/day-retreats",
    scarcityNote: "10am–2pm · limited to 10 guests",
    priceLabel: "£80 per person",
    status: "sold-out",
  },
  {
    id: "portugal-may-2026",
    type: "partner",
    format: "residential",
    partnerName: "Yoga on the Edge",
    partnerLogo: "/images/yogaontheedge.webp",
    name: "Ember and Earth",
    location: "Portugal",
    startDate: "2026-05-02",
    endDate: "2026-05-09",
    displayDates: "2–9 May 2026",
    heroImage: "/images/portugal.webp",
    description:
      "Yin meets Hatha and Ashtanga-inspired flow. Build inner strength while staying deeply rooted in calm, clarity, and connection.",
    forYouIf: "movement and yoga are at the heart of what you need.",
    priceLabel: "Priced by Yoga on the Edge",
    bookLink: "https://yogaontheedge.co.uk/workshops-and-retreats/workshops-and-retreats/",
    brochureLink:
      "https://yogaontheedge.co.uk/wp-content/uploads/2025/07/YOTE-2026-Retreats-Brochure-mp4-2.mp4",
    isExternalBook: true,
  },
  {
    id: "portugal-sept-2026",
    type: "partner",
    format: "residential",
    partnerName: "Yoga on the Edge",
    partnerLogo: "/images/yogaontheedge.webp",
    name: "Breathe & Breakthrough",
    location: "Portugal",
    startDate: "2026-09-26",
    endDate: "2026-10-03",
    displayDates: "26 Sept – 3 Oct 2026",
    heroImage: "/images/portugal.webp",
    description:
      "Ashtanga, Yin, Rocket, and creative Vinyasa flow. Reconnect with your breath and break through personal limitations — on and off the mat.",
    forYouIf:
      "you want to challenge yourself physically and mentally through movement.",
    priceLabel: "Priced by Yoga on the Edge",
    bookLink: "https://yogaontheedge.co.uk/workshops-and-retreats/workshops-and-retreats/",
    brochureLink: "/images/YOTE2026RetreatsBrochure.pdf",
    isExternalBook: true,
  },
];

/**
 * Upcoming retreats that have their own page on this site, for the sitemap.
 *
 * Derived rather than hand-listed so a finished retreat drops out of the sitemap on
 * its own — the Summer Solstice page stayed indexed for eight weeks after the event
 * because the sitemap kept a separate hardcoded copy of this list.
 */
export function getUpcomingRetreatPages(): { path: string; lastmod: string }[] {
  return getUpcomingRetreats()
    .filter((r) => r.pageLastmod && r.bookLink.startsWith("/retreats/"))
    .map((r) => ({ path: r.bookLink, lastmod: r.pageLastmod! }));
}

export function getUpcomingRetreats(): Retreat[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return retreats
    .filter((r) => new Date(r.endDate) >= today)
    .sort(
      (a, b) =>
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    );
}

/** The short UK retreats — half days, days and weekends. */
export function isShortRetreat(r: Retreat): boolean {
  return r.format === "day" || r.format === "weekend";
}

/** Short retreats that have finished, most recent first — shown as past retreats. */
export function getPastShortRetreats(): Retreat[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return retreats
    .filter((r) => isShortRetreat(r) && new Date(r.endDate) < today)
    .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
}

/**
 * Register-interest lists for retreats that have no date yet.
 *
 * A dated retreat page expires with its date — that is what left
 * /retreats/autumn-equinox-sept-2026 serving a past Event to search for weeks.
 * These lists don't expire: someone asks for "a weekend retreat", not for a
 * date, and when Niki schedules one the list is who hears first. The id is
 * accepted by `/api/retreat-interest` in place of a retreat id (always as a
 * waitlist capture — there is nothing to book or send a brochure for), and is
 * the `retreat_waitlist` event label, so day-vs-weekend demand is countable.
 */
export type RetreatInterestList = {
  id: string;
  /** The option as the visitor picks it. */
  label: string;
  /** How the confirmation email names what they signed up for. */
  emailName: string;
};

export const RETREAT_INTEREST_LISTS: RetreatInterestList[] = [
  { id: "uk-day-retreat", label: "Day retreat", emailName: "UK day retreats" },
  { id: "uk-weekend-retreat", label: "Weekend retreat", emailName: "UK weekend retreats" },
  { id: "uk-any-retreat", label: "Either", emailName: "UK day and weekend retreats" },
];

export function getRetreatInterestList(id: string): RetreatInterestList | undefined {
  return RETREAT_INTEREST_LISTS.find((l) => l.id === id);
}

/**
 * Whether a retreat should still be offering a way to pay.
 *
 * The single test for "swap the CTA for the waitlist". Kept here rather than
 * inline in the components because it is asked in four places — the hero, the
 * featured block, the CF card grid and the partner card grid — and a sold-out
 * retreat that stays bookable in one of them is the whole bug.
 */
export function isBookable(retreat: Retreat): boolean {
  return retreat.status !== "sold-out";
}
